import {
  Area,
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Customized,
  LabelList,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';
import { monthNamesRu } from '@app/utils/quarters';
import { CustomDot } from './hashrate-dynamics-dot';
import { CustomQuarterLabel } from './hashrate-dynamics-quarter-label';
import { CustomMonthlyLabel } from './hashrate-dynamics-monthly-label';
import { getHorizontalCoordinates } from './getHorizontalCoordinates';
import { getYAxisTicks } from './getYAxisTicks';

type ChartDataItem = {
  name: string;
  monthValue: number;
  quarterValue: number;
};

type Props = {
  data: Array<ChartDataItem>;
  isLoading?: boolean;
  showMonths?: boolean;
  showQuarters?: boolean;
};

const AXIS_TICK_STYLE = { fill: '#B8B8B9', fontSize: 12 };

export const LineGraph = ({ data, showMonths = true, showQuarters = false }: Props) => {
  const chartData =
    data.length > 0 ? data : monthNamesRu.map(name => ({ name, monthValue: 0, quarterValue: 0 }));

  const allValues = [
    ...chartData.map(chartItem => chartItem.monthValue),
    ...chartData.map(chartItem => chartItem.quarterValue),
  ];

  const rawMaxValue = Math.max(...allValues);
  const safeMaxValue = rawMaxValue > 0 ? rawMaxValue : 1;
  const yAxisTicks = getYAxisTicks({ maxValue: safeMaxValue });
  const yAxisMaxDomain = yAxisTicks[yAxisTicks.length - 1] ?? safeMaxValue;

  return (
    <ResponsiveContainer width="100%" height={300}>
      <ComposedChart
        data={chartData}
        margin={{ top: 30, right: 30, left: 20, bottom: 5 }}
        barCategoryGap={2}
        barGap={0}
      >
        <defs>
          <linearGradient
            id="hashrateGradient"
            x1="0"
            y1="0"
            x2="0"
            y2="1"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0%" stopColor="#7086FD" stopOpacity={0.8} />
            <stop offset="50%" stopColor="#7086FD" stopOpacity={0.6} />
            <stop offset="100%" stopColor="#7086FD" stopOpacity={0.12} />
          </linearGradient>
        </defs>
        <XAxis
          dataKey="name"
          type="category"
          scale="band"
          interval={0}
          minTickGap={0}
          padding={{ left: -2, right: 0 }}
          tickLine={false}
          axisLine={true}
          tick={props => {
            const { x, y, payload, width } = props;
            const bandWidth = width ? width / chartData.length : 0;

            return (
              <text
                x={x + bandWidth / 2}
                y={y + 5}
                textAnchor="middle"
                fill={AXIS_TICK_STYLE.fill}
                fontSize={AXIS_TICK_STYLE.fontSize}
              >
                {payload.value}
              </text>
            );
          }}
        />
        <YAxis
          domain={[0, yAxisMaxDomain]}
          ticks={yAxisTicks}
          tick={AXIS_TICK_STYLE}
          tickLine={false}
          axisLine={false}
        />
        <CartesianGrid
          strokeDasharray="3 3"
          horizontal
          vertical
          stroke="#4F4E50"
          fill="transparent"
          horizontalCoordinatesGenerator={({ yAxis }) =>
            getHorizontalCoordinates({
              ticks: yAxis?.ticks,
              scale: yAxis?.scale,
            })
          }
        />
        {showQuarters && (
          <>
            <Bar dataKey="quarterValue">
              {chartData.map((_, index) => (
                <Cell key={`cell-${index}`} fill="#6FD195" fillOpacity={0.06} />
              ))}
            </Bar>
            <Customized component={CustomQuarterLabel} />
          </>
        )}
        {showMonths && (
          <Area
            type="linear"
            dataKey="monthValue"
            stroke="#7086FD"
            strokeWidth={2}
            fill="url(#hashrateGradient)"
            dot={props => {
              const { key, ...restProps } = props;
              return <CustomDot key={key} {...restProps} />;
            }}
          >
            <LabelList
              dataKey="monthValue"
              position={showQuarters ? "bottom" : "top"}
              content={props => {
                const { key, ...restProps } = props;
                return <CustomMonthlyLabel key={key} {...restProps} positionBelow={showQuarters} />;
              }}
            />
          </Area>
        )}
      </ComposedChart>
    </ResponsiveContainer>
  );
};
